import { type StandingTeam } from "@/api/tournamentAnalysis";
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { dateFormat } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/shadcn/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/shadcn/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SLOT_HEIGHT = 120;

interface TeamInfo {
    id: number;
    name: string;
    seed: number;
    wins: number;
}

interface MatchRecord {
    date: string;
    opponentTeamName: string;
    result: string;
    score: string;
    winnerTeamId: number;
}

interface Matchup {
    team1: TeamInfo;
    team2: TeamInfo;
    winnerId: number | null;
    matches: MatchRecord[];
}

interface PlayoffBracketProps {
    standings: StandingTeam[];
}

function getTeamInitials(name: string): string {
    return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
}

function getTeamColor(seed: number): string {
    const colors = [
        "bg-primary",
        "bg-blue-500",
        "bg-purple-500",
        "bg-pink-500",
        "bg-rose-500",
        "bg-orange-500",
        "bg-yellow-500",
        "bg-green-500",
    ];
    return colors[(seed - 1) % colors.length] ?? "bg-surface-container-high";
}

const TEAM_LOGO_MAP: Record<string, string> = {
    "Frisk Asker": "Frisk-Asker.png",
    Storhamar: "Storhamar.png",
    Sparta: "Sparta-Sarpsborg.png",
    Vålerenga: "Valerenga.png",
    Stjernen: "Stjernen.png",
    Stavanger: "Stavanger-Oilers.png",
    Narvik: "Narvik-Hockey-Dark.png",
    Lillehammer: "Lillehammer.png",
    Nidaros: "Nidaros.png",
    Grüner: "Gruner.png",
    Gruner: "Gruner.png",
    Comet: "Comet.png",
    Ringerike: "Ringerike.png",
    "Manglerud Star": "Manglerud-Star.png",
    "Lørenskog IK": "Lorenskog.png",
    Lørenskog: "Lorenskog.png",
    "Comet Halden": "Comet.png",
};

function getTeamLogoUrl(teamName: string): string {
    const logoFilename = TEAM_LOGO_MAP[teamName] || `${teamName.replace(/\s+/g, "-")}.png`;
    return `/team-logos/${logoFilename}`;
}

function buildPlayoffBracket(standings: StandingTeam[]): Matchup[][] {
    if (standings.length === 0) return [];

    const gamesByMatchId = new Map<
        number,
        {
            team1Id: number;
            team2Id: number;
            team1Name: string;
            team2Name: string;
            winner: number;
            date: string;
            opponentName: string;
            score: string;
            result: string;
        }
    >();

    standings.forEach((team) => {
        team.recentForm.forEach((match) => {
            if (!gamesByMatchId.has(match.matchId)) {
                const isWin = ["W", "WO", "WP"].includes(match.result.toUpperCase());
                gamesByMatchId.set(match.matchId, {
                    team1Id: team.teamId,
                    team2Id: match.opponentTeamId,
                    team1Name: team.teamName,
                    team2Name: match.opponentTeamName,
                    winner: isWin ? team.teamId : match.opponentTeamId,
                    date: match.date,
                    opponentName: match.opponentTeamName,
                    score: match.score,
                    result: match.result,
                });
            }
        });
    });

    const allGames = Array.from(gamesByMatchId.values()).sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const seriesMap = new Map<
        string,
        {
            team1Id: number;
            team1Name: string;
            team2Id: number;
            team2Name: string;
            team1Wins: number;
            team2Wins: number;
            firstGameDate: string;
            matches: MatchRecord[];
        }
    >();

    allGames.forEach((game) => {
        const pairKey = [game.team1Id, game.team2Id].sort().join("-");
        const team1IsSmaller = game.team1Id < game.team2Id;
        const series =
            seriesMap.get(pairKey) ||
            (() => {
                const newSeries = {
                    team1Id: Math.min(game.team1Id, game.team2Id),
                    team1Name: team1IsSmaller ? game.team1Name : game.team2Name,
                    team2Id: Math.max(game.team1Id, game.team2Id),
                    team2Name: team1IsSmaller ? game.team2Name : game.team1Name,
                    team1Wins: 0,
                    team2Wins: 0,
                    firstGameDate: game.date,
                    matches: [] as MatchRecord[],
                };
                seriesMap.set(pairKey, newSeries);
                return newSeries;
            })();

        if (series.team1Wins < 4 && series.team2Wins < 4) {
            if (game.winner === series.team1Id) {
                series.team1Wins++;
            } else {
                series.team2Wins++;
            }
        }

        series.matches.push({
            date: game.date,
            opponentTeamName: game.opponentName,
            result: game.result,
            score: game.score,
            winnerTeamId: game.winner,
        });
    });

    const sortedSeries = Array.from(seriesMap.values()).sort(
        (a, b) => new Date(a.firstGameDate).getTime() - new Date(b.firstGameDate).getTime()
    );

    const rounds: Matchup[][] = [];
    let seriesIndex = 0;
    let seriesPerRound = Math.max(1, Math.floor(standings.length / 2));

    while (seriesIndex < sortedSeries.length) {
        const roundSeries = sortedSeries.slice(seriesIndex, seriesIndex + seriesPerRound);
        const roundMatchups: Matchup[] = roundSeries.map((series) => {
            const team1Data = standings.find((s) => s.teamId === series.team1Id);
            const team2Data = standings.find((s) => s.teamId === series.team2Id);

            return {
                team1: {
                    id: series.team1Id,
                    name: series.team1Name,
                    seed: team1Data?.position ?? 0,
                    wins: series.team1Wins,
                },
                team2: {
                    id: series.team2Id,
                    name: series.team2Name,
                    seed: team2Data?.position ?? 0,
                    wins: series.team2Wins,
                },
                winnerId:
                    series.team1Wins > series.team2Wins
                        ? series.team1Id
                        : series.team2Wins > series.team1Wins
                          ? series.team2Id
                          : null,
                matches: series.matches,
            };
        });

        rounds.push(roundMatchups);
        seriesIndex += seriesPerRound;
        seriesPerRound = Math.max(1, Math.floor(seriesPerRound / 2));
    }

    return rounds;
}

function TeamLogo({ teamName, teamSeed }: { teamName: string; teamSeed: number }) {
    const [logoError, setLogoError] = useState(false);
    const logoUrl = getTeamLogoUrl(teamName);

    if (logoError || teamName === "TBD") {
        return (
            <div
                className={`flex items-center justify-center w-8 h-8 rounded text-xs font-bold text-white shrink-0 ${getTeamColor(teamSeed)}`}
            >
                {getTeamInitials(teamName)}
            </div>
        );
    }

    return (
        <img
            src={logoUrl}
            alt={teamName}
            className="w-8 h-8 object-contain shrink-0"
            onError={() => setLogoError(true)}
        />
    );
}

function MatchupCard({ matchup }: { matchup: Matchup }) {
    const [dialogOpen, setDialogOpen] = useState(false);

    const cardContent = (
        <div className="rounded-lg border border-outline-variant/30 bg-surface-container-low overflow-hidden shadow-sm w-56">
            <div
                className={`flex items-center gap-3 px-3 py-2 ${
                    matchup.winnerId === matchup.team1.id ? "font-bold" : "opacity-60"
                }`}
            >
                <TeamLogo teamName={matchup.team1.name} teamSeed={matchup.team1.seed} />
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm">{matchup.team1.name}</div>
                    <div className="text-xs text-on-surface-variant">({matchup.team1.seed})</div>
                </div>
                <div className="text-sm font-mono shrink-0">{matchup.team1.wins}</div>
            </div>
            <div
                className={`flex items-center gap-3 px-3 py-2 border-t border-outline-variant/30 ${
                    matchup.winnerId === matchup.team2.id ? "font-bold" : "opacity-60"
                }`}
            >
                <TeamLogo teamName={matchup.team2.name} teamSeed={matchup.team2.seed} />
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm">{matchup.team2.name}</div>
                    <div className="text-xs text-on-surface-variant">({matchup.team2.seed})</div>
                </div>
                <div className="text-sm font-mono shrink-0">{matchup.team2.wins}</div>
            </div>
        </div>
    );

    const matchesContent = (
        <table className="w-full border-collapse">
            <thead>
                <tr className="border-b border-outline-variant/20">
                    <th className="w-8 px-4 py-2 text-[9px] font-headline font-black uppercase tracking-widest text-on-surface-variant text-center">
                        #
                    </th>
                    <th className="px-3 py-2 text-[9px] font-headline font-black uppercase tracking-widest text-on-surface-variant text-left">
                        Winner
                    </th>
                    <th className="px-3 py-2 text-[9px] font-headline font-black uppercase tracking-widest text-on-surface-variant text-center">
                        Score
                    </th>
                    <th className="px-4 py-2 text-[9px] font-headline font-black uppercase tracking-widest text-on-surface-variant text-right">
                        Date
                    </th>
                </tr>
            </thead>
            <tbody>
                {matchup.matches.map((match, idx) => {
                    const winnerTeam = match.winnerTeamId === matchup.team1.id ? matchup.team1 : matchup.team2;
                    const isWin = match.result.toUpperCase() === "W" || match.result.toUpperCase() === "WO";
                    const rowBg = idx % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                    return (
                        <tr key={idx} className={`border-b border-outline-variant/10 last:border-0 ${rowBg}`}>
                            <td className="w-8 px-4 py-2.5 text-xs font-bold text-on-surface-variant tabular-nums text-center">
                                {idx + 1}
                            </td>
                            <td className="px-3 py-2.5">
                                <div className="flex items-center gap-2 min-w-0">
                                    <TeamLogo teamName={winnerTeam.name} teamSeed={winnerTeam.seed} />
                                    <span className="hidden md:block text-sm font-semibold text-on-surface truncate">
                                        {winnerTeam.name}
                                    </span>
                                </div>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                                <span
                                    className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                                        isWin ? "bg-win-background text-win" : "bg-loss-background text-loss"
                                    }`}
                                >
                                    {match.score}
                                </span>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-on-surface-variant whitespace-nowrap text-right">
                                {dayjs(match.date).format(dateFormat)}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    if (matchup.matches.length === 0) {
        return cardContent;
    }

    return (
        <>
            <div className="hidden md:block">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div>{cardContent}</div>
                    </TooltipTrigger>
                    <TooltipContent
                        side="right"
                        className="p-0 max-w-sm bg-surface-container-lowest border border-outline-variant/20 rounded-xl shadow-2xl overflow-hidden"
                    >
                        <div>{matchesContent}</div>
                    </TooltipContent>
                </Tooltip>
            </div>
            <button type="button" className="md:hidden text-left" onClick={() => setDialogOpen(true)}>
                {cardContent}
            </button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="p-0 w-[calc(100vw-2rem)] max-w-sm bg-surface-container-lowest border border-outline-variant/20 rounded-xl shadow-2xl overflow-hidden">
                    <DialogHeader className="px-5 py-4 border-b border-outline-variant/10 bg-surface-container-low">
                        <DialogTitle className="text-on-surface font-semibold text-base">
                            {matchup.team1.name} vs {matchup.team2.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[60vh]">{matchesContent}</div>
                </DialogContent>
            </Dialog>
        </>
    );
}

function BracketConnectorSide({
    numMatchups,
    slotHeight,
    mirrored = false,
}: {
    numMatchups: number;
    slotHeight: number;
    mirrored?: boolean;
}) {
    const totalHeight = numMatchups * slotHeight;
    const width = 32;
    const midX = width / 2;
    const pairs = Math.floor(numMatchups / 2);
    return (
        <svg
            width={width}
            height={totalHeight}
            className="shrink-0 self-start overflow-visible"
            style={{ display: "block" }}
        >
            <g
                style={{
                    stroke: "var(--color-outline-variant)",
                    fill: "none",
                    strokeWidth: 1.5,
                    strokeLinecap: "round",
                }}
            >
                {Array.from({ length: pairs }).map((_, i) => {
                    const y1 = 2 * i * slotHeight + slotHeight / 2;
                    const y2 = (2 * i + 1) * slotHeight + slotHeight / 2;
                    const midY = (y1 + y2) / 2;
                    return (
                        <g key={i}>
                            {mirrored ? (
                                <>
                                    <line x1={width} y1={midY} x2={midX} y2={midY} />
                                    <line x1={midX} y1={y1} x2={midX} y2={y2} />
                                    <line x1={midX} y1={y1} x2={0} y2={y1} />
                                    <line x1={midX} y1={y2} x2={0} y2={y2} />
                                </>
                            ) : (
                                <>
                                    <line x1={0} y1={y1} x2={midX} y2={y1} />
                                    <line x1={0} y1={y2} x2={midX} y2={y2} />
                                    <line x1={midX} y1={y1} x2={midX} y2={y2} />
                                    <line x1={midX} y1={midY} x2={width} y2={midY} />
                                </>
                            )}
                        </g>
                    );
                })}
            </g>
        </svg>
    );
}

function BracketConnector({ numMatchups, slotHeight }: { numMatchups: number; slotHeight: number }) {
    const totalHeight = numMatchups * slotHeight;
    const width = 48;
    const midX = width / 2;
    const pairs = Math.floor(numMatchups / 2);

    return (
        <div className="flex flex-col items-center gap-3 shrink-0">
            <div className="text-sm font-semibold invisible select-none" aria-hidden>
                X
            </div>
            <svg width={width} height={totalHeight} className="shrink-0 overflow-visible">
                <g
                    style={{
                        stroke: "var(--color-outline-variant)",
                        fill: "none",
                        strokeWidth: 1.5,
                        strokeLinecap: "round",
                    }}
                >
                    {Array.from({ length: pairs }).map((_, i) => {
                        const y1 = 2 * i * slotHeight + slotHeight / 2;
                        const y2 = (2 * i + 1) * slotHeight + slotHeight / 2;
                        const midY = (y1 + y2) / 2;
                        return (
                            <g key={i}>
                                <line x1={0} y1={y1} x2={midX} y2={y1} />
                                <line x1={0} y1={y2} x2={midX} y2={y2} />
                                <line x1={midX} y1={y1} x2={midX} y2={y2} />
                                <line x1={midX} y1={midY} x2={width} y2={midY} />
                            </g>
                        );
                    })}
                </g>
            </svg>
        </div>
    );
}

function BracketRound({
    matchups,
    slotHeight,
    roundIndex,
}: {
    matchups: Matchup[];
    slotHeight: number;
    roundIndex: number;
}) {
    return (
        <div className="flex flex-col items-center gap-3">
            <div className="text-sm font-semibold text-on-surface-variant uppercase tracking-wide">
                {getRoundLabel(roundIndex)}
            </div>
            <div
                className="relative shrink-0"
                style={{
                    height: `${matchups.length * slotHeight}px`,
                    width: "224px",
                }}
            >
                {matchups.map((matchup, idx) => (
                    <div
                        key={idx}
                        className="absolute left-0 right-0"
                        style={{
                            top: 0,
                            height: `${slotHeight}px`,
                            transform: `translateY(${idx * slotHeight}px)`,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <MatchupCard matchup={matchup} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function getRoundLabel(index: number, totalRounds: number = 3): string {
    const stepsFromFinal = totalRounds - 1 - index;
    if (stepsFromFinal === 0) return "Final";
    if (stepsFromFinal === 1) return "1/2 Final";
    if (stepsFromFinal === 2) return "1/4 Final";
    return `Round ${index + 1}`;
}

export function PlayoffBracket({ standings }: PlayoffBracketProps) {
    const rounds = useMemo(() => buildPlayoffBracket(standings), [standings]);
    const [mobileRound, setMobileRound] = useState(0);
    const [slideDir, setSlideDir] = useState<"left" | "right" | null>(null);

    const navigateTo = (next: number) => {
        if (next === mobileRound) return;
        setSlideDir(next > mobileRound ? "left" : "right");
        setMobileRound(next);
    };

    if (rounds.length === 0) {
        return (
            <div className="flex items-center justify-center py-8 text-center text-on-surface-variant">
                <div>
                    <div className="text-sm font-medium mb-1">No playoff data available</div>
                    <div className="text-xs">Select a playoff tournament to view the bracket</div>
                </div>
            </div>
        );
    }

    const currentMatchups = rounds[mobileRound] ?? [];
    const slideClass =
        slideDir === "left" ? "animate-slide-in-left" : slideDir === "right" ? "animate-slide-in-right" : "";

    return (
        <>
            <style>{`
                @keyframes slideInLeft {
                    from { transform: translateX(40px); opacity: 0; }
                    to   { transform: translateX(0);    opacity: 1; }
                }
                @keyframes slideInRight {
                    from { transform: translateX(-40px); opacity: 0; }
                    to   { transform: translateX(0);     opacity: 1; }
                }
                .animate-slide-in-left  { animation: slideInLeft  0.25s ease-out both; }
                .animate-slide-in-right { animation: slideInRight 0.25s ease-out both; }
            `}</style>
            <div className="md:hidden bg-surface-container-lowest px-4 py-4">
                <div className="flex items-center justify-between mb-3">
                    <button
                        type="button"
                        onClick={() => navigateTo(mobileRound - 1)}
                        disabled={mobileRound === 0}
                        className="p-1.5 rounded-lg bg-surface-container disabled:opacity-30 transition-opacity"
                        aria-label="Previous round"
                    >
                        <ChevronLeft className="w-5 h-5 text-on-surface" />
                    </button>
                    <span className="text-sm font-bold uppercase tracking-widest text-on-surface-variant">
                        {getRoundLabel(mobileRound, rounds.length)}
                    </span>
                    <button
                        type="button"
                        onClick={() => navigateTo(mobileRound + 1)}
                        disabled={mobileRound === rounds.length - 1}
                        className="p-1.5 rounded-lg bg-surface-container disabled:opacity-30 transition-opacity"
                        aria-label="Next round"
                    >
                        <ChevronRight className="w-5 h-5 text-on-surface" />
                    </button>
                </div>
                <div className="overflow-hidden">
                    <div key={mobileRound} className={`flex items-start justify-center ${slideClass}`}>
                        {mobileRound > 0 && (
                            <BracketConnectorSide
                                numMatchups={currentMatchups.length * 2}
                                slotHeight={SLOT_HEIGHT * Math.pow(2, mobileRound - 1)}
                                mirrored={true}
                            />
                        )}
                        <div
                            className="relative"
                            style={{
                                height: `${currentMatchups.length * SLOT_HEIGHT * Math.pow(2, mobileRound)}px`,
                                width: "224px",
                            }}
                        >
                            {currentMatchups.map((matchup, idx) => {
                                const slotHeight = SLOT_HEIGHT * Math.pow(2, mobileRound);
                                return (
                                    <div
                                        key={idx}
                                        className="absolute left-0 right-0 flex items-center"
                                        style={{
                                            top: `${idx * slotHeight}px`,
                                            height: `${slotHeight}px`,
                                        }}
                                    >
                                        <MatchupCard matchup={matchup} />
                                    </div>
                                );
                            })}
                        </div>
                        {mobileRound < rounds.length - 1 && (
                            <BracketConnectorSide
                                numMatchups={currentMatchups.length}
                                slotHeight={SLOT_HEIGHT * Math.pow(2, mobileRound)}
                            />
                        )}
                    </div>
                </div>
                {rounds.length > 1 && (
                    <div className="flex justify-center gap-1.5 mt-4">
                        {rounds.map((_, idx) => (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => navigateTo(idx)}
                                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                                    idx === mobileRound ? "bg-primary" : "bg-outline-variant"
                                }`}
                                aria-label={getRoundLabel(idx, rounds.length)}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="hidden md:block overflow-auto bg-surface-container-lowest p-8">
                <div className="flex items-start justify-center" style={{ minWidth: "fit-content", margin: "0 auto" }}>
                    {rounds.map((roundMatchups, roundIdx) => (
                        <>
                            <BracketRound
                                key={`round-${roundIdx}`}
                                matchups={roundMatchups}
                                slotHeight={SLOT_HEIGHT * Math.pow(2, roundIdx)}
                                roundIndex={roundIdx}
                            />
                            {roundIdx < rounds.length - 1 && (
                                <BracketConnector
                                    key={`connector-${roundIdx}`}
                                    numMatchups={roundMatchups.length}
                                    slotHeight={SLOT_HEIGHT * Math.pow(2, roundIdx)}
                                />
                            )}
                        </>
                    ))}
                </div>
            </div>
        </>
    );
}
